/**
 * Form per la creazione di una fattura (attiva o passiva).
 *
 * La direzione viene determinata dal query param `direzione`.
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUO } from '@shell/UOContext';

/* ------------------------------------------------------------------ */
/* Tipi                                                                */
/* ------------------------------------------------------------------ */

interface RigaForm {
  descrizione: string;
  quantita: string;
  unita_misura: string;
  prezzo_unitario: string;
  aliquota_iva_percentuale: string;
  natura_iva_codice: string;
  articolo_id: string | null;
}

interface Soggetto {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  partita_iva: string | null;
  codice_fiscale: string | null;
  pec: string | null;
  codice_sdi: string | null;
}

interface Articolo {
  id: string;
  sku_interno: string;
  descrizione: string;
  giacenza: number | null;
}

interface Classificazione {
  id: string;
  codice: string;
  descrizione: string;
  meta: Record<string, unknown> | null;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
  attivo?: boolean;
}

/** Voce del dominio ModalitaPagamento (elemento 2.4.2.2 FatturaPA). Il dominio
 *  e' chiuso dalla norma e arriva dal backend: qui non si elenca nulla. */
interface ModalitaPagamentoDominio {
  codice: string;
  descrizione: string;
  /** Vero dove senza IBAN il cliente non puo' materialmente pagare. Non e' un
   *  obbligo SDI: nessun controllo dello SDI tocca il blocco DatiPagamento. */
  richiede_iban: boolean;
  /** Falso dove siamo NOI a non saperlo ancora emettere correttamente. */
  esponibile_ui: boolean;
}

/** Voce del dominio CondizioniPagamento (elemento 2.4.1 FatturaPA). */
interface CondizioniPagamentoDominio {
  codice: string;
  descrizione: string;
  esponibile_ui: boolean;
}

/** Condizione di pagamento COMMERCIALE, configurata in Impostazioni Azienda >
 *  Pagamenti. E' quella che si sceglie su una fattura; `mp_codice` e
 *  `tp_codice` sono la sua traduzione nei due domini FatturaPA, impostata
 *  sempre da quella pagina. Dove mancano, la fattura non sa cosa dichiarare
 *  allo SDI e la scelta va completata li', non indovinata qui. */
interface CondizionePagamentoConfigurata {
  id: string;
  codice: string;
  descrizione: string;
  attiva: boolean;
  mp_codice: string | null;
  tp_codice: string | null;
}

/* ------------------------------------------------------------------ */
/* Costanti                                                            */
/* ------------------------------------------------------------------ */

const RIGA_VUOTA: RigaForm = {
  descrizione: '',
  quantita: '1',
  unita_misura: 'pz',
  prezzo_unitario: '0',
  aliquota_iva_percentuale: '22',
  natura_iva_codice: '',
  articolo_id: null,
};

/** Stati da cui una fattura si riporta in bozza per correggerla e rinviarla
 *  con lo STESSO numero: non e' mai stata emessa, quindi non c'e' nulla da
 *  stornare. Allineato a stati_fattura.STATI_RECUPERABILI del backend. */
const STATI_RECUPERABILI = ['scartata', 'rifiutata'];

const TIPI_DOCUMENTO = [
  { codice: 'TD01', etichetta: 'Fattura' },
  { codice: 'TD04', etichetta: 'Nota di credito' },
  { codice: 'TD05', etichetta: 'Nota di debito' },
  { codice: 'TD06', etichetta: 'Parcella' },
  { codice: 'TD24', etichetta: 'Fattura differita' },
];

/** Durata debounce in ms per le ricerche. */
const DEBOUNCE_MS = 300;

/* ------------------------------------------------------------------ */
/* Hook: ricerca debounced su endpoint API                             */
/* ------------------------------------------------------------------ */

/**
 * Hook per ricerca debounced su un endpoint API.
 *
 * @param endpoint - URL base dell'API
 * @param minChars - Caratteri minimi per avviare la ricerca
 * @returns Stato e funzioni per gestire la ricerca
 */
function useApiSearch<T>(endpoint: string, minChars: number = 1) {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      if (query.trim().length < minChars) {
        setResults([]);
        return;
      }

      timerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const separator = endpoint.includes('?') ? '&' : '?';
          const params = new URLSearchParams({ q: query.trim(), limit: '20' });
          const url = `${endpoint}${separator}${params.toString()}`;
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
            },
          });
          if (response.ok) {
            const data: T[] = await response.json();
            setResults(data);
          }
        } catch {
          /* errore di rete: ignora silenziosamente */
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [endpoint, minChars],
  );

  const clear = useCallback(() => {
    setResults([]);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { results, loading, search, clear };
}

/* ------------------------------------------------------------------ */
/* Hook: carica classificazioni da API                                 */
/* ------------------------------------------------------------------ */

/**
 * Carica classificazioni per un dato sistema dall'API.
 *
 * @param sistema - Nome del sistema classificazioni (es. "unita_misura")
 * @returns Lista di classificazioni caricate
 */
function useClassificazioni(sistema: string): Classificazione[] {
  const [items, setItems] = useState<Classificazione[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(
          `/api/v1/classificazioni?sistema=${encodeURIComponent(sistema)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
            },
          },
        );
        if (response.ok) {
          const data: Classificazione[] = await response.json();
          setItems(data);
        }
      } catch {
        /* errore di rete: ignora */
      }
    };
    load();
  }, [sistema]);

  return items;
}

/* ------------------------------------------------------------------ */
/* Utility                                                             */
/* ------------------------------------------------------------------ */

/**
 * Restituisce la denominazione leggibile di un soggetto.
 *
 * @param s - Soggetto da cui estrarre la denominazione
 * @returns Stringa con ragione sociale o nome/cognome
 */
function denomSoggetto(s: Soggetto): string {
  return s.ragione_sociale || [s.nome, s.cognome].filter(Boolean).join(' ');
}

/* --------------------------------------------------------------------------
 * Aritmetica esatta sugli importi.
 *
 * Qui NON si usano ne' `number` ne' `parseFloat` ne' `Math.round`, per tre
 * ragioni misurate, non teoriche:
 *  - `parseFloat("1.005") * 100` fa 100.49999999999999 in IEEE 754, quindi
 *    `Math.round` restituisce 1.00 dove la regola fiscale vuole 1.01;
 *  - `Math.round` e' "meta' verso +infinito", non HALF_UP: su una nota di
 *    credito con imposta esatta -5.005 da' -5.00 mentre il backend (Decimal
 *    ROUND_HALF_UP) da' -5.01. Frontend e backend divergevano sul segno;
 *  - le specifiche impongono l'arrotondamento al centesimo con la terza cifra
 *    decimale come discriminante (Elenco controlli SDI, controllo 00421).
 *
 * Si lavora su interi (BigInt) scalati a 8 decimali, che e' la precisione
 * massima che lo schema FatturaPA ammette su PrezzoUnitario e PrezzoTotale
 * (Amount8DecimalType).
 * ------------------------------------------------------------------------ */

/** Fattore di scala: 8 decimali, come Amount8DecimalType. */
const SCALA = 100000000n;

/** Converte una stringa decimale in intero scalato, senza passare da float. */
function aScalato(s: string): bigint {
  const testo = (s ?? '').trim().replace(',', '.');
  if (!testo) return 0n;
  const negativo = testo.startsWith('-');
  const pulito = testo.replace(/^[+-]/, '');
  if (!/^\d*\.?\d*$/.test(pulito) || pulito === '' || pulito === '.') return 0n;
  const [intera, decimale = ''] = pulito.split('.');
  const dec8 = (decimale + '00000000').slice(0, 8);
  const v = BigInt(intera || '0') * SCALA + BigInt(dec8);
  return negativo ? -v : v;
}

/** Arrotonda uno scalato a 2 decimali con HALF UP (allontanandosi da zero),
 *  restituendo i CENTESIMI come intero. E' l'unico arrotondamento ammesso. */
function aCentesimiHalfUp(scalato: bigint): bigint {
  const divisore = SCALA / 100n; // da 8 decimali a 2
  const negativo = scalato < 0n;
  const assoluto = negativo ? -scalato : scalato;
  const quoziente = assoluto / divisore;
  const resto = assoluto % divisore;
  // meta' esatta o piu' -> si sale (per eccesso), come chiede il controllo 00421
  const arrotondato = resto * 2n >= divisore ? quoziente + 1n : quoziente;
  return negativo ? -arrotondato : arrotondato;
}

/** Da centesimi interi al numero da mostrare e da inviare. */
function daCentesimi(c: bigint): number {
  return Number(c) / 100;
}

/**
 * Importo di una singola riga: (quantita x prezzo unitario), al centesimo.
 *
 * Corrisponde a PrezzoTotale (2.2.1.11), che il controllo SDI 00423 verifica
 * contro quantita x prezzo unitario.
 */
function importoRigaCentesimi(r: RigaForm): bigint {
  const prodotto = (aScalato(r.quantita) * aScalato(r.prezzo_unitario)) / SCALA;
  return aCentesimiHalfUp(prodotto);
}

/** Versione in euro, per la visualizzazione. */
function importoRiga(r: RigaForm): number {
  return daCentesimi(importoRigaCentesimi(r));
}

/* NOTA: `ivaRiga` NON esiste piu', e non e' stata "corretta": era un concetto
 * che nel tracciato FatturaPA non esiste. Non c'e' nessuna imposta a livello di
 * riga; l'imposta e' un dato UNICO per aliquota, calcolato sull'imponibile gia'
 * aggregato (art. 21 c.2 lett. l e c.3 DPR 633/72; controllo SDI 00421:
 * Imposta = AliquotaIVA * ImponibileImporto / 100).
 * Sommare imposte arrotondate riga per riga produceva un errore SISTEMATICO
 * per difetto, crescente col numero di righe: su tre righe da 59,70 al 22%
 * dava 39,39 invece di 39,40. */

/* ------------------------------------------------------------------ */
/* Componente principale                                               */
/* ------------------------------------------------------------------ */

/**
 * Form creazione fattura passiva o attiva.
 */
const FatturaForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Presente solo sulla rotta /documentale/fatture/:id/modifica: la stessa
  // pagina serve a creare e a correggere, perche' i campi sono gli stessi e
  // mantenerne due copie e' il modo sicuro per farle divergere.
  const { id: idModifica } = useParams<{ id: string }>();
  const annoModifica = searchParams.get('year') ?? String(new Date().getFullYear());
  const inModifica = Boolean(idModifica);
  const [caricamentoIniziale, setCaricamentoIniziale] = useState(false);
  /** La fattura e' stata scartata o rifiutata: va riportata in bozza prima di
   *  poterla correggere. Il passaggio esiste gia' lato backend. */
  const [recuperabile, setRecuperabile] = useState(false);
  const [recupero, setRecupero] = useState(false);
  // Il ramo emittente: non si sceglie da qui (il backend lo prende dal token),
  // ma va almeno DETTO, perche' determina l'IBAN di incasso della fattura.
  const { currentUO, availableUOs } = useUO();
  /* In CREAZIONE la direzione arriva dalla URL. In MODIFICA deve venire dalla
   * fattura caricata: prendendola dalla URL (dove non c'e') si ripiegava su
   * 'passiva' e la pagina mostrava "Fornitore (cedente)" su una fattura di
   * vendita, nascondendo per giunta i campi di pagamento, che esistono solo
   * sulle attive. I valori salvati erano giusti, l'interfaccia mentiva. */
  const [direzioneFattura, setDirezioneFattura] = useState<string | null>(null);
  const direzione = direzioneFattura ?? searchParams.get('direzione') ?? 'passiva';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Classificazioni da API */
  const unitaMisura = useClassificazioni('unita_misura');
  const aliquoteIva = useClassificazioni('aliquote_iva');

  /* Ordina aliquote per percentuale crescente */
  const aliquoteOrdinate = [...aliquoteIva].sort((a, b) => {
    const pa = (a.meta?.percentuale as number) ?? parseFloat(a.codice) ?? 0;
    const pb = (b.meta?.percentuale as number) ?? parseFloat(b.codice) ?? 0;
    return pa - pb;
  });

  /* Campi comuni */
  const [dataDocumento, setDataDocumento] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [tipoDocumento, setTipoDocumento] = useState('TD01');

  /* Centri di costo */
  const [centri, setCentri] = useState<Centro[]>([]);
  const [centroCostoId, setCentroCostoId] = useState('');
  // Testo digitato nel campo: il Combobox non filtra da solo, riceve le opzioni
  // gia' scremate (stesso pattern del dettaglio fattura).
  const [centroFiltro, setCentroFiltro] = useState('');
  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/centri-costo/centri?limit=200', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (res.ok) {
        const d = await res.json();
        setCentri(Array.isArray(d) ? d : (d.items ?? []));
      }
    })();
  }, []);

  /* Dati di pagamento -------------------------------------------------------
   * Fino al 2026-09-14 non erano scegliibili: il backend applicava in silenzio
   * MP05 (bonifico) / TP02 e l'utente non lo sapeva. Ogni fattura emessa
   * dichiarava "bonifico" anche quando l'incasso era di altra natura.
   * Il dominio dei codici e' CHIUSO dalla norma e vive a database: si legge,
   * non si configura. */
  const [modalitaPagamento, setModalitaPagamento] = useState('MP05');
  const [condizioniPagamento, setCondizioniPagamento] = useState('TP02');
  /** Voce COMMERCIALE scelta dall'utente, dalle Impostazioni Azienda (tab
   *  Pagamenti). E' questa che si sceglie su una fattura: "Bonifico 30 gg
   *  DFFM", non "MP05". I codici della norma si ricavano da qui. */
  const [condizioneId, setCondizioneId] = useState('');
  const [condizioniConfigurate, setCondizioniConfigurate] = useState<
    CondizionePagamentoConfigurata[]
  >([]);
  const [condizioneFiltro, setCondizioneFiltro] = useState('');
  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/config/modalita-pagamento', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (res.ok) {
        const d = await res.json();
        setCondizioniConfigurate(
          (Array.isArray(d) ? d : []).filter(
            (x: CondizionePagamentoConfigurata) => x.attiva
          )
        );
      }
    })();
  }, []);
  const [ibanPagamento, setIbanPagamento] = useState('');
  const [dataScadenza, setDataScadenza] = useState('');
  const [modalitaDominio, setModalitaDominio] = useState<ModalitaPagamentoDominio[]>([]);
  const [condizioniDominio, setCondizioniDominio] = useState<CondizioniPagamentoDominio[]>([]);
  const [modalitaFiltro, setModalitaFiltro] = useState('');
  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/fatture/lookups/fatturapa-pagamento', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (res.ok) {
        const d = await res.json();
        setModalitaDominio(d.modalita ?? []);
        setCondizioniDominio(d.condizioni ?? []);
      }
    })();
  }, []);

  const modalitaScelta = modalitaDominio.find((m) => m.codice === modalitaPagamento);
  /** L'IBAN non e' imposto da nessun controllo SDI: e' necessario in FATTO,
   *  perche' senza, con queste modalita', il cliente non puo' pagare. */
  const ibanNecessario = modalitaScelta?.richiede_iban ?? false;
  const modalitaOptions: ComboboxOption[] = (() => {
    const q = modalitaFiltro.trim().toLowerCase();
    return modalitaDominio
      .filter((m) => m.esponibile_ui)
      .filter((m) => !q || `${m.codice} ${m.descrizione}`.toLowerCase().includes(q))
      .map((m) => ({ value: m.codice, label: `${m.codice} - ${m.descrizione}` }));
  })();

  const modalitaSelezionataLabel = modalitaScelta
    ? `${modalitaScelta.codice} - ${modalitaScelta.descrizione}`
    : '';

  /* La fattura persiste i CODICI (mp/tp), non quale voce commerciale fu
   * scelta: non esiste una colonna che la ricordi. Riaprendo una bozza, la
   * tendina resterebbe vuota su una fattura che i dati di pagamento ce li ha.
   * La si ricava all'indietro dai codici, quando la corrispondenza e' unica. */
  const condizioneScelta =
    condizioniConfigurate.find((c) => c.id === condizioneId) ??
    (() => {
      if (condizioneId) return undefined;
      const candidate = condizioniConfigurate.filter(
        (c) => c.mp_codice === modalitaPagamento && c.tp_codice === condizioniPagamento
      );
      return candidate.length === 1 ? candidate[0] : undefined;
    })();
  /** Una condizione e' usabile in fattura solo se qualcuno le ha detto come si
   *  traduce nei codici della norma. Altrimenti si emetterebbe un documento con
   *  una dichiarazione inventata. */
  const condizioneMappata = Boolean(
    condizioneScelta?.mp_codice && condizioneScelta?.tp_codice
  );
  const condizioneNormativa = condizioniDominio.find(
    (c) => c.codice === condizioniPagamento
  );
  const condizioneSelezionataLabel = condizioneScelta
    ? condizioneScelta.descrizione
    : '';
  const condizioniOptions: ComboboxOption[] = (() => {
    const q = condizioneFiltro.trim().toLowerCase();
    return condizioniConfigurate
      .filter((c) => !q || c.descrizione.toLowerCase().includes(q))
      .map((c) => ({
        value: c.id,
        label: c.mp_codice && c.tp_codice
          ? c.descrizione
          : `${c.descrizione} (da completare in Impostazioni)`,
      }));
  })();

  /** Etichetta del centro attualmente scelto, mostrata quando non si sta
   *  digitando: senza, il campo sembrerebbe vuoto dopo la selezione. */
  const centroSelezionatoLabel = (() => {
    const c = centri.find((x) => x.id === centroCostoId);
    return c ? `${c.codice} - ${c.nome}` : '';
  })();

  /** Opzioni filtrate live sul testo digitato. La voce di azzeramento compare
   *  solo se c'e' qualcosa da azzerare. */
  const centriOptions: ComboboxOption[] = (() => {
    const q = centroFiltro.trim().toLowerCase();
    const trovati = centri
      .filter((c) => c.attivo !== false)
      .filter((c) => !q || `${c.codice} ${c.nome}`.toLowerCase().includes(q))
      .map((c) => ({ value: c.id, label: `${c.codice} - ${c.nome}` }));
    return centroCostoId
      ? [{ value: '', label: 'Nessun centro di costo' }, ...trovati]
      : trovati;
  })();

  /* Controparte */
  const [denominazione, setDenominazione] = useState('');
  const [soggettoId, setSoggettoId] = useState<string | null>(null);
  const [piva, setPiva] = useState('');
  const [cf, setCf] = useState('');
  const [codiceSdi, setCodiceSdi] = useState('');
  const [pec, setPec] = useState('');

  /* Emittente (per attive) */
  const [cedenteDenom, setCedenteDenom] = useState('');
  const [cedentePiva, setCedentePiva] = useState('');
  const [cedenteCf, setCedenteCf] = useState('');

  /* Numero fornitore (per passive) */
  const [numeroFornitore, setNumeroFornitore] = useState('');

  /* Righe */
  const [righe, setRighe] = useState<RigaForm[]>([{ ...RIGA_VUOTA }]);

  /* Caricamento della fattura da correggere ------------------------------- */
  useEffect(() => {
    if (!idModifica) return;
    setCaricamentoIniziale(true);
    void (async () => {
      const h = { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` };
      try {
        const [resF, resR] = await Promise.all([
          fetch(`/api/v1/fatture/${idModifica}?year=${annoModifica}`, { headers: h }),
          fetch(`/api/v1/fatture/${idModifica}/righe?year=${annoModifica}`, {
            headers: h,
          }),
        ]);
        if (!resF.ok) throw new Error(`Fattura non caricata (${resF.status})`);
        const f = await resF.json();
        if (f.stato !== 'bozza') {
          // Una SCARTATA non e' mai stata emessa: si corregge e si rinvia con
          // lo stesso numero, non si storna. Dire "serve una nota di credito"
          // sarebbe falso e manderebbe a emettere un documento inutile.
          if (STATI_RECUPERABILI.includes(f.stato)) {
            setRecuperabile(true);
            setError(null);
          } else {
            setError(
              `Questa fattura e' in stato "${f.stato}" e non si modifica. ` +
                'Un documento gia\' emesso si rettifica con una nota di credito.'
            );
          }
          return;
        }
        setDirezioneFattura(f.direzione ?? null);
        setDataDocumento(f.data ?? '');
        setTipoDocumento(f.tipo_documento_codice ?? 'TD01');
        setDenominazione(f.cessionario_denominazione ?? '');
        setPiva(f.cessionario_piva ?? '');
        setCf(f.cessionario_cf ?? '');
        setCodiceSdi(f.cessionario_codice_sdi ?? '');
        setPec(f.cessionario_pec ?? '');
        setCedenteDenom(f.cedente_denominazione ?? '');
        setCedentePiva(f.cedente_piva ?? '');
        setCedenteCf(f.cedente_cf ?? '');
        setCentroCostoId(f.centro_costo_id ?? '');
        if (f.modalita_pagamento) setModalitaPagamento(f.modalita_pagamento);
        if (f.condizioni_pagamento) setCondizioniPagamento(f.condizioni_pagamento);
        if (f.condizione_pagamento_id) setCondizioneId(f.condizione_pagamento_id);
        setIbanPagamento(f.iban_pagamento ?? '');
        setDataScadenza(f.data_scadenza ?? '');
        if (resR.ok) {
          const rr = await resR.json();
          if (Array.isArray(rr) && rr.length > 0) {
            setRighe(
              rr.map((r: Record<string, unknown>) => ({
                ...RIGA_VUOTA,
                descrizione: String(r.descrizione ?? ''),
                quantita: r.quantita != null ? String(r.quantita) : '',
                unita_misura: String(r.unita_misura ?? ''),
                prezzo_unitario:
                  r.prezzo_unitario != null ? String(r.prezzo_unitario) : '',
                // Normalizzata come le opzioni del Select, che valgono
                // String(perc) cioe' "22". Dall'API arriva "22.00": senza
                // questo passaggio il confronto fallisce, la tendina appare
                // VUOTA su una riga che ha eccome la sua aliquota, e chi
                // salva rischia di crederla da riempire.
                aliquota_iva_percentuale:
                  r.aliquota_iva_percentuale != null &&
                  !Number.isNaN(Number(r.aliquota_iva_percentuale))
                    ? String(Number(r.aliquota_iva_percentuale))
                    : '',
                natura_iva_codice: String(r.natura_iva_codice ?? ''),
              }))
            );
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Errore nel caricamento');
      } finally {
        setCaricamentoIniziale(false);
      }
    })();
    // Si carica una volta sola all'apertura: ricaricare mentre l'utente digita
    // cancellerebbe le sue modifiche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idModifica]);

  /* Indice riga attiva per autocomplete articoli */
  const [rigaAttivaIdx, setRigaAttivaIdx] = useState<number | null>(null);

  /* Ricerca soggetti */
  const tipoSoggetto = direzione === 'passiva' ? 'fornitore' : 'cliente';
  const soggettiSearch = useApiSearch<Soggetto>(
    `/api/v1/soggetti?tipo=${tipoSoggetto}`,
    1,
  );

  /* Ricerca articoli */
  const articoliSearch = useApiSearch<Articolo>(
    '/api/v1/magazzino/articoli',
    1,
  );

  /* Opzioni soggetti per il Combobox */
  const soggettiOptions: ComboboxOption[] = soggettiSearch.results.map((s) => ({
    value: s.id,
    label: denomSoggetto(s),
    sublabel:
      [
        s.partita_iva ? `P.IVA ${s.partita_iva}` : null,
        s.codice_fiscale ? `CF ${s.codice_fiscale}` : null,
      ]
        .filter(Boolean)
        .join(' — ') || undefined,
  }));

  /* Opzioni articoli per il Combobox */
  const articoliOptions: ComboboxOption[] = articoliSearch.results.map((a) => ({
    value: a.id,
    label: a.descrizione,
    sublabel: `SKU ${a.sku_interno}${a.giacenza != null ? ` — Giac. ${a.giacenza}` : ''}`,
  }));

  /* ---------------------------------------------------------------- */
  /* Handler                                                           */
  /* ---------------------------------------------------------------- */

  /**
   * Gestisce la selezione di un soggetto dall'autocomplete.
   *
   * @param opt - Opzione selezionata
   */
  const handleSoggettoSelect = (opt: ComboboxOption) => {
    const s = soggettiSearch.results.find((r) => r.id === opt.value);
    if (!s) return;
    setDenominazione(denomSoggetto(s));
    setSoggettoId(s.id);
    setPiva(s.partita_iva ?? '');
    setCf(s.codice_fiscale ?? '');
    setPec(s.pec ?? '');
    setCodiceSdi(s.codice_sdi ?? '');
    soggettiSearch.clear();
  };

  /**
   * Gestisce il cambio del testo nel campo denominazione.
   *
   * @param v - Nuovo valore del campo
   */
  const handleDenomChange = (v: string) => {
    setDenominazione(v);
    setSoggettoId(null);
    soggettiSearch.search(v);
  };

  const aggiungiRiga = () => setRighe((prev) => [...prev, { ...RIGA_VUOTA }]);

  /**
   * Aggiorna uno o più campi di una riga specifica.
   *
   * @param index - Indice della riga
   * @param updates - Oggetto con i campi da aggiornare
   */
  const aggiornaRigaCampi = (index: number, updates: Partial<RigaForm>) => {
    setRighe((prev) => prev.map((r, i) => (i === index ? { ...r, ...updates } : r)));
  };

  /**
   * Aggiorna un singolo campo di una riga.
   *
   * @param index - Indice della riga
   * @param campo - Nome del campo da aggiornare
   * @param valore - Nuovo valore
   */
  const aggiornaRiga = (index: number, campo: keyof RigaForm, valore: string) => {
    aggiornaRigaCampi(index, { [campo]: valore });
  };

  const rimuoviRiga = (index: number) => {
    if (righe.length <= 1) return;
    setRighe((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Gestisce la selezione di un articolo da magazzino per una riga.
   *
   * @param index - Indice della riga
   * @param opt - Opzione selezionata
   */
  const handleArticoloSelect = (index: number, opt: ComboboxOption) => {
    const a = articoliSearch.results.find((r) => r.id === opt.value);
    if (!a) return;
    aggiornaRigaCampi(index, { descrizione: a.descrizione, articolo_id: a.id });
    articoliSearch.clear();
  };

  /* ---------------------------------------------------------------- */
  /* Calcoli importi                                                   */
  /* ---------------------------------------------------------------- */

  /**
   * Totali del documento, con la regola dei DatiRiepilogo.
   *
   * Le righe si aggregano per chiave (aliquota, natura): per ogni gruppo si
   * somma l'imponibile SENZA arrotondare durante la somma, e solo alla fine si
   * calcola l'imposta con UN SOLO arrotondamento. E' l'unico punto del
   * documento in cui un'imposta viene arrotondata.
   *
   * Questa e' un'ANTEPRIMA: l'autorita' sui totali e' del backend, che li
   * ricalcola dalle righe e rifiuta la creazione se quelli dichiarati qui non
   * coincidono al centesimo. Le due implementazioni devono dare lo stesso
   * numero, ed e' esattamente cio' che il confronto lato server verifica.
   */
  const calcolaImporti = () => {
    const perChiave = new Map<string, { imponibile: bigint; aliquota: bigint }>();
    for (const r of righe) {
      const aliquota = aScalato(r.aliquota_iva_percentuale);
      const chiave = `${r.aliquota_iva_percentuale ?? ''}|${r.natura_iva_codice ?? ''}`;
      const gruppo = perChiave.get(chiave) ?? { imponibile: 0n, aliquota };
      gruppo.imponibile += importoRigaCentesimi(r);
      perChiave.set(chiave, gruppo);
    }

    let imponibileTot = 0n;
    let ivaTot = 0n;
    for (const g of perChiave.values()) {
      imponibileTot += g.imponibile;
      // imponibile e' in CENTESIMI, aliquota e' scalata a 8 decimali.
      //   imposta_euro     = (imp_cent / 100) * (aliq_scal / 1e8) / 100
      //   imposta_scalata  = imposta_euro * 1e8 = imp_cent * aliq_scal / 1e4
      // Poi un solo arrotondamento al centesimo.
      const impostaScalata = (g.imponibile * g.aliquota) / 10000n;
      ivaTot += aCentesimiHalfUp(impostaScalata);
    }

    return {
      imponibile: daCentesimi(imponibileTot),
      iva: daCentesimi(ivaTot),
      totale: daCentesimi(imponibileTot + ivaTot),
    };
  };

  const importi = calcolaImporti();

  /** Art. 49 c.1 D.Lgs 231/2007: divieto di trasferimento di contante fra
   *  soggetti diversi per importi PARI O SUPERIORI a 5.000 euro (soglia in
   *  vigore dal 01/01/2023). Non e' un controllo SDI: e' una norma che sta
   *  fuori dalla fattura, e nessuno la ricorderebbe al momento giusto. */
  const avvisoContante = modalitaPagamento === 'MP01' && importi.totale >= 5000;

  /* ---------------------------------------------------------------- */
  /* Salvataggio                                                       */
  /* ---------------------------------------------------------------- */

  /**
   * Salva la fattura (passiva o attiva) tramite API.
   */
  /** Riporta in bozza una fattura scartata e ricarica la pagina in modifica.
   *  Usa l'endpoint che gia' esiste per il recupero dopo uno scarto. */
  const riportaInBozza = async () => {
    if (!idModifica) return;
    setRecupero(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/fatture/${idModifica}/riporta-in-bozza?year=${annoModifica}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(
          typeof b?.detail === 'string' ? b.detail : `Errore ${res.status}`
        );
      }
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recupero non riuscito');
      setRecupero(false);
    }
  };

  const salva = async () => {
    setLoading(true);
    setError(null);

    try {
      const righePayload = righe.map((r, idx) => ({
        numero_linea: idx + 1,
        descrizione: r.descrizione,
        quantita: parseFloat(r.quantita) || null,
        unita_misura: r.unita_misura || null,
        prezzo_unitario: parseFloat(r.prezzo_unitario) || null,
        aliquota_iva_percentuale: parseFloat(r.aliquota_iva_percentuale) || null,
        natura_iva_codice: r.natura_iva_codice || null,
        importo_riga: importoRiga(r),
      }));

      // MODIFICA di una bozza esistente: si sostituiscono le righe (il server
      // ricalcola i totali dalle righe, non li accetta da qui) e si aggiorna
      // l'anagrafica. Due chiamate perche' sono due contratti diversi, non
      // perche' siano due operazioni: se la prima fallisce, la seconda non
      // parte e la fattura resta com'era.
      if (idModifica) {
        const token = localStorage.getItem('token') ?? '';
        const intestazione = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };
        const resRighe = await fetch(
          `/api/v1/fatture/${idModifica}/righe?year=${annoModifica}`,
          {
            method: 'PUT',
            headers: intestazione,
            body: JSON.stringify({ righe: righePayload }),
          }
        );
        if (!resRighe.ok) {
          const b = await resRighe.json().catch(() => null);
          throw new Error(
            typeof b?.detail === 'string' ? b.detail : `Errore ${resRighe.status}`
          );
        }
        const resTestata = await fetch(
          `/api/v1/fatture/${idModifica}?year=${annoModifica}`,
          {
            method: 'PATCH',
            headers: intestazione,
            body: JSON.stringify({
              data_documento: dataDocumento,
              cessionario_denominazione: denominazione,
              cessionario_piva: piva || null,
              cessionario_cf: cf || null,
              cessionario_codice_sdi: codiceSdi || null,
              cessionario_pec: pec || null,
              cedente_denominazione: cedenteDenom,
              cedente_piva: cedentePiva || null,
              cedente_cf: cedenteCf || null,
              modalita_pagamento: modalitaPagamento || null,
              condizioni_pagamento: condizioniPagamento || null,
              condizione_pagamento_id: condizioneId || null,
              iban_pagamento: ibanPagamento || null,
            }),
          }
        );
        if (!resTestata.ok) {
          const b = await resTestata.json().catch(() => null);
          throw new Error(
            typeof b?.detail === 'string'
              ? b.detail
              : `Righe salvate, ma la testata no (errore ${resTestata.status})`
          );
        }
        navigate(`/documentale/fatture/${idModifica}?year=${annoModifica}`);
        return;
      }

      let body: Record<string, unknown>;
      let endpoint: string;

      if (direzione === 'passiva') {
        endpoint = '/api/v1/fatture/passive';
        body = {
          data_documento: dataDocumento,
          tipo_documento_codice: tipoDocumento,
          cedente_denominazione: denominazione,
          cedente_piva: piva || null,
          cedente_cf: cf || null,
          numero_fornitore: numeroFornitore || null,
          importo_imponibile: importi.imponibile,
          importo_iva: importi.iva,
          importo_totale: importi.totale,
          centro_costo_id: centroCostoId || null,
          righe: righePayload,
        };
      } else {
        endpoint = '/api/v1/fatture/attive';
        body = {
          data_documento: dataDocumento,
          tipo_documento_codice: tipoDocumento,
          cessionario_soggetto_id: soggettoId,
          cessionario_denominazione: denominazione,
          cessionario_piva: piva || null,
          cessionario_cf: cf || null,
          cessionario_codice_sdi: codiceSdi || null,
          cessionario_pec: pec || null,
          cedente_denominazione: cedenteDenom,
          cedente_piva: cedentePiva || null,
          cedente_cf: cedenteCf || null,
          importo_imponibile: importi.imponibile,
          importo_iva: importi.iva,
          importo_totale: importi.totale,
          centro_costo_id: centroCostoId || null,
          modalita_pagamento: modalitaPagamento || null,
          condizioni_pagamento: condizioniPagamento || null,
          condizione_pagamento_id: condizioneId || null,
          iban_pagamento: ibanPagamento || null,
          data_scadenza: dataScadenza || null,
          righe: righePayload,
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${response.status}`);
      }

      navigate('/documentale/fatture');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {inModifica
            ? 'Modifica bozza'
            : direzione === 'passiva'
              ? 'Registra fattura passiva'
              : 'Crea fattura attiva'}
        </h1>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Annulla
        </Button>
      </div>

      {inModifica && (
        <div className="rounded border bg-muted/40 p-3 text-sm">
          Stai correggendo una bozza: puoi cambiare righe, importi, controparte e
          dati di pagamento. Salvando, i totali vengono ricalcolati dalle righe e
          i rilievi fiscali aperti su questa fattura vengono riaperti al
          controllo successivo. Il numero del documento non cambia.
        </div>
      )}

      {recuperabile && (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2">
          <p>
            <strong>Questa fattura e&apos; stata scartata dallo SDI.</strong> Non
            risulta emessa: si corregge e si rinvia con lo stesso numero, non serve
            nessuna nota di credito. Per modificarla va prima riportata in bozza.
          </p>
          <Button
            size="sm"
            disabled={recupero}
            onClick={() => void riportaInBozza()}
          >
            {recupero ? 'Attendere...' : 'Riporta in bozza e correggi'}
          </Button>
        </div>
      )}

      {caricamentoIniziale && (
        <div className="rounded border bg-muted/40 p-3 text-sm">
          Caricamento della fattura...
        </div>
      )}

      {/* Finche' la fattura e' scartata i suoi dati non sono stati caricati:
          mostrare il form sotto significherebbe esporre campi vuoti e una
          controparte etichettata a caso. Si vede solo la via d'uscita. */}
      {recuperabile ? null : (
        <>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Dati documento */}
      <Card>
        <CardHeader>
          <CardTitle>Dati documento</CardTitle>
        </CardHeader>
        {/* Il RAMO non era scritto da nessuna parte in questa pagina, eppure
            decide quale IBAN d'incasso finisce nella fattura (il backend lo
            prende dal token con current_user.uo_id, mai da questo form). Con
            due rami e due IBAN si emetteva alla cieca. */}
        {direzione === 'attiva' && (
          <div className="mx-6 mb-4 rounded border bg-muted/40 p-3 text-sm">
            Stai emettendo per il ramo{' '}
            <strong>{currentUO?.nome ?? 'non determinato'}</strong>
            {currentUO
              ? ". L'IBAN di incasso della fattura sara' quello configurato per questo ramo."
              : ". Senza un ramo nel token la fattura nasce senza IBAN d'incasso: scegli il ramo dalla barra in alto prima di proseguire."}
            {availableUOs.length > 1 && currentUO
              ? ' Per cambiarlo, usa il selettore del ramo nella barra in alto.'
              : ''}
          </div>
        )}
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label>Data documento</Label>
            <Input
              type="date"
              value={dataDocumento}
              onChange={(e) => setDataDocumento(e.target.value)}
            />
          </div>
          <div>
            <Label>Tipo documento</Label>
            <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPI_DOCUMENTO.map((t) => (
                  <SelectItem key={t.codice} value={t.codice}>
                    {t.codice} — {t.etichetta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {direzione === 'passiva' && (
            <div>
              <Label>Numero fattura fornitore</Label>
              <Input
                value={numeroFornitore}
                onChange={(e) => setNumeroFornitore(e.target.value)}
                placeholder="Es. 2026/0042"
              />
            </div>
          )}
          {direzione === 'attiva' && (
            <>
              <div className="md:col-span-2">
                <Label>Condizioni di pagamento</Label>
                {/* Le condizioni si governano da Impostazioni Azienda > tab
                    Pagamenti: qui si SCEGLIE fra quelle configurate, non se ne
                    inventano. I codici FatturaPA che finiranno nell'XML sono la
                    traduzione della voce scelta, impostata sempre la'. */}
                <Combobox
                  value={condizioneFiltro || condizioneSelezionataLabel}
                  onInputChange={setCondizioneFiltro}
                  onSelect={(opt) => {
                    setCondizioneFiltro('');
                    setCondizioneId(opt.value);
                    const c = condizioniConfigurate.find((x) => x.id === opt.value);
                    if (c?.mp_codice) setModalitaPagamento(c.mp_codice);
                    if (c?.tp_codice) setCondizioniPagamento(c.tp_codice);
                  }}
                  options={condizioniOptions}
                  placeholder="Cerca o scegli una condizione di pagamento..."
                  ariaLabel="Condizioni di pagamento"
                  /* Senza questa, il campo si apre SOLO digitando: al click
                     restava muto e sembrava una casella di testo libera. */
                  openOnFocus
                />
                {condizioneScelta && !condizioneMappata && (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    Questa condizione non ha ancora la corrispondenza con i codici
                    della fattura elettronica. Impostala in Impostazioni Azienda,
                    tab Pagamenti: senza, non si sa cosa dichiarare allo SDI.
                  </p>
                )}
                {condizioneMappata && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Nell&apos;XML andra&apos;: {modalitaPagamento}
                    {modalitaScelta ? ` (${modalitaScelta.descrizione})` : ''} e{' '}
                    {condizioniPagamento}
                    {condizioneNormativa ? ` (${condizioneNormativa.descrizione})` : ''}.
                  </p>
                )}
                {avvisoContante && (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    Contante pari o superiore a 5.000 EUR: vietato fra soggetti
                    diversi (art. 49 c.1 D.Lgs 231/2007).
                  </p>
                )}
              </div>
              <div>
                <Label>Data scadenza</Label>
                <Input
                  type="date"
                  value={dataScadenza}
                  onChange={(e) => setDataScadenza(e.target.value)}
                />
              </div>
              <div>
                <Label>
                  IBAN di incasso{ibanNecessario ? ' (necessario)' : ' (facoltativo)'}
                </Label>
                <Input
                  value={ibanPagamento}
                  onChange={(e) => setIbanPagamento(e.target.value)}
                  placeholder={
                    ibanNecessario
                      ? "Senza IBAN il cliente non puo' pagare"
                      : 'Lascia vuoto per usare quello del ramo'
                  }
                />
                {ibanNecessario && !ibanPagamento && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Se lo lasci vuoto viene usato l&apos;IBAN configurato per il
                    ramo emittente. Con questa modalita&apos; un IBAN ci vuole.
                  </p>
                )}
              </div>
            </>
          )}
          <div>
            <Label>Centro di costo</Label>
            {/* Era un <select> HTML nudo con bg-transparent: il menu aperto
                risultava illeggibile (testo chiaro su fondo chiaro) e non aveva
                ricerca. Lo stesso identico campo, nel dettaglio fattura, era
                gia' un Combobox: ora i due comandi coincidono. */}
            <Combobox
              value={centroFiltro || centroSelezionatoLabel}
              onInputChange={setCentroFiltro}
              onSelect={(opt) => {
                setCentroFiltro('');
                setCentroCostoId(opt.value);
              }}
              options={centriOptions}
              placeholder="Cerca o scegli un centro di costo..."
              ariaLabel="Centro di costo"
              openOnFocus
            />
          </div>
        </CardContent>
      </Card>

      {/* Controparte */}
      <Card>
        <CardHeader>
          <CardTitle>
            {direzione === 'passiva' ? 'Fornitore (cedente)' : 'Cliente (cessionario)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Denominazione / Ragione sociale</Label>
            <Combobox
              value={denominazione}
              onInputChange={handleDenomChange}
              onSelect={handleSoggettoSelect}
              options={soggettiOptions}
              loading={soggettiSearch.loading}
              placeholder={
                direzione === 'passiva'
                  ? 'Cerca fornitore per nome o P.IVA...'
                  : 'Cerca cliente per nome o P.IVA...'
              }
            />
          </div>
          <div>
            <Label>Partita IVA</Label>
            <Input
              value={piva}
              onChange={(e) => setPiva(e.target.value)}
              maxLength={11}
            />
          </div>
          <div>
            <Label>Codice fiscale</Label>
            <Input
              value={cf}
              onChange={(e) => setCf(e.target.value)}
              maxLength={16}
            />
          </div>
          {direzione === 'attiva' && (
            <>
              <div>
                <Label>Codice SDI</Label>
                <Input
                  value={codiceSdi}
                  onChange={(e) => setCodiceSdi(e.target.value)}
                  maxLength={7}
                />
              </div>
              <div>
                <Label>PEC</Label>
                <Input
                  value={pec}
                  onChange={(e) => setPec(e.target.value)}
                  type="email"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Emittente (solo per attive) */}
      {direzione === 'attiva' && (
        <Card>
          <CardHeader>
            <CardTitle>Emittente (cedente)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label>Denominazione</Label>
              <Input
                value={cedenteDenom}
                onChange={(e) => setCedenteDenom(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Partita IVA</Label>
              <Input
                value={cedentePiva}
                onChange={(e) => setCedentePiva(e.target.value)}
                maxLength={11}
              />
            </div>
            <div>
              <Label>Codice fiscale</Label>
              <Input
                value={cedenteCf}
                onChange={(e) => setCedenteCf(e.target.value)}
                maxLength={16}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Righe */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Righe</CardTitle>
          <Button variant="outline" size="sm" onClick={aggiungiRiga}>
            + Aggiungi riga
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {righe.map((riga, idx) => {
            const impRiga = importoRiga(riga);

            return (
              <div
                key={idx}
                className="space-y-2 rounded-lg border p-3"
              >
                {/* Riga superiore: prodotto, qtà, UM, prezzo */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Label className="text-xs">Prodotto / Descrizione</Label>
                    <Combobox
                      value={riga.descrizione}
                      onInputChange={(v) => {
                        aggiornaRigaCampi(idx, { descrizione: v, articolo_id: null });
                        setRigaAttivaIdx(idx);
                        articoliSearch.search(v);
                      }}
                      onSelect={(opt) => handleArticoloSelect(idx, opt)}
                      options={rigaAttivaIdx === idx ? articoliOptions : []}
                      loading={rigaAttivaIdx === idx ? articoliSearch.loading : false}
                      placeholder="Cerca prodotto..."
                    />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Qtà</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={riga.quantita}
                      onChange={(e) => aggiornaRiga(idx, 'quantita', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Unità misura</Label>
                    <Select
                      value={riga.unita_misura}
                      onValueChange={(v) => aggiornaRiga(idx, 'unita_misura', v)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="UM" />
                      </SelectTrigger>
                      <SelectContent>
                        {unitaMisura.map((um) => (
                          <SelectItem key={um.codice} value={um.codice}>
                            {um.codice.toUpperCase()} — {um.descrizione}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Prezzo unit.</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={riga.prezzo_unitario}
                      onChange={(e) =>
                        aggiornaRiga(idx, 'prezzo_unitario', e.target.value)
                      }
                    />
                  </div>
                  <div className="col-span-1 flex items-end justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => rimuoviRiga(idx)}
                      disabled={righe.length <= 1}
                      className="text-destructive"
                    >
                      ✕
                    </Button>
                  </div>
                </div>

                {/* Riga inferiore: IVA, natura, importi calcolati */}
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-xs">Aliquota IVA</Label>
                    <Select
                      value={riga.aliquota_iva_percentuale}
                      onValueChange={(v) =>
                        aggiornaRiga(idx, 'aliquota_iva_percentuale', v)
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="IVA %" />
                      </SelectTrigger>
                      <SelectContent>
                        {aliquoteOrdinate.map((al) => {
                          const perc =
                            (al.meta?.percentuale as number) ??
                            parseFloat(al.codice) ??
                            0;
                          return (
                            <SelectItem key={al.codice} value={String(perc)}>
                              {perc}% — {al.descrizione}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Natura IVA</Label>
                    <Input
                      value={riga.natura_iva_codice}
                      onChange={(e) =>
                        aggiornaRiga(idx, 'natura_iva_codice', e.target.value)
                      }
                      placeholder="N1..."
                    />
                  </div>
                  <div className="col-span-7 flex items-end justify-end gap-6 pb-1">
                    <span className="text-xs text-muted-foreground">
                      Imponibile:{' '}
                      <span className="font-mono font-medium text-foreground">
                        {impRiga.toFixed(2)} €
                      </span>
                    </span>
                    {/* "IVA di riga" e "Totale di riga" non si mostrano piu':
                        non esistono nel tracciato e mostrarli induceva a
                        sommarli, che e' il difetto da cui nasceva il centesimo
                        mancante. L'imposta e' un dato per ALIQUOTA e si legge
                        nel riepilogo in fondo alla pagina. */}
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Totali e salva */}
      <Card>
        <CardContent className="flex items-center justify-between pt-6">
          <div className="flex gap-8 font-mono text-sm">
            <div>
              <span className="text-muted-foreground">Imponibile: </span>
              <span className="font-semibold">
                {importi.imponibile.toFixed(2)} €
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">IVA: </span>
              <span className="font-semibold">{importi.iva.toFixed(2)} €</span>
            </div>
            <div>
              <span className="text-muted-foreground">Totale: </span>
              <span className="text-lg font-bold">
                {importi.totale.toFixed(2)} €
              </span>
            </div>
          </div>
          <Button onClick={salva} disabled={loading || !denominazione}>
            {loading ? 'Salvataggio…' : 'Salva fattura'}
          </Button>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};

export default FatturaForm;
